/**
 * Document upload page.
 *
 * After case creation and personal details collection the applicant uploads
 * identity documents. The required upload slots are determined by the
 * selected document type (passport, national ID card, driver's license,
 * residence permit). A selfie slot and proof of address slot are always
 * present.
 *
 * Document type selection uses visual cards with SVG icons indicating
 * whether front-only or front-and-back scans are needed.
 *
 * @module pages/UploadDocumentsPage
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { applicant } from '@noxverify/api-contracts';
import type { ApiClientError } from '@noxverify/api-client';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getApiClient, uploadFileToSignedUrl } from '../lib/api';
import { StepIndicator } from '../components/StepIndicator';
import { FileUpload } from '../components/FileUpload';
import { DocumentCapture } from '../components/DocumentCapture';
import { Footer } from '../components/Footer';

// ---------------------------------------------------------------------------
// Document type icons (inline SVGs)
// ---------------------------------------------------------------------------

function PassportIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
}

function IdCardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"
      />
    </svg>
  );
}

function CarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
      />
    </svg>
  );
}

function HouseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Document type configuration
// ---------------------------------------------------------------------------

type DocumentType = 'passport' | 'national_id_card' | 'drivers_license' | 'residence_permit';

interface DocumentTypeOption {
  value: DocumentType;
  label: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
}

function getDocumentTypes(t: TFunction): DocumentTypeOption[] {
  return [
    {
      value: 'passport',
      label: t('uploadDocuments.documentTypes.passport.label'),
      subtitle: t('uploadDocuments.documentTypes.passport.subtitle'),
      icon: PassportIcon,
    },
    {
      value: 'national_id_card',
      label: t('uploadDocuments.documentTypes.national_id_card.label'),
      subtitle: t('uploadDocuments.documentTypes.national_id_card.subtitle'),
      icon: IdCardIcon,
    },
    {
      value: 'drivers_license',
      label: t('uploadDocuments.documentTypes.drivers_license.label'),
      subtitle: t('uploadDocuments.documentTypes.drivers_license.subtitle'),
      icon: CarIcon,
    },
    {
      value: 'residence_permit',
      label: t('uploadDocuments.documentTypes.residence_permit.label'),
      subtitle: t('uploadDocuments.documentTypes.residence_permit.subtitle'),
      icon: HouseIcon,
    },
  ];
}

// ---------------------------------------------------------------------------
// Upload slot definitions
// ---------------------------------------------------------------------------

interface UploadSlot {
  key: string;
  label: string;
  evidenceType: applicant.EvidenceType;
  required: boolean;
}

/** Build the list of document slots based on the selected document type. */
function getDocumentSlots(
  docType: DocumentType | null,
  requireProofOfAddress: boolean,
  t: TFunction,
): UploadSlot[] {
  if (!docType) return [];

  const slots: UploadSlot[] = [];

  // ID front is always required
  slots.push({
    key: 'id-front',
    label: getIdFrontLabel(docType, t),
    evidenceType: 'identity_document_front',
    required: true,
  });

  // ID back required for all types except passport
  if (docType !== 'passport') {
    slots.push({
      key: 'id-back',
      label: getIdBackLabel(docType, t),
      evidenceType: 'identity_document_back',
      required: true,
    });
  }

  // Selfie
  slots.push({
    key: 'selfie',
    label: t('uploadDocuments.slots.selfie'),
    evidenceType: 'selfie',
    required: true,
  });

  // Proof of address
  slots.push({
    key: 'poa',
    label: t('uploadDocuments.slots.proofOfAddress'),
    evidenceType: 'proof_of_address',
    required: requireProofOfAddress,
  });

  return slots;
}

function getIdFrontLabel(docType: DocumentType, t: TFunction): string {
  switch (docType) {
    case 'passport':
      return t('uploadDocuments.slots.passportFront');
    case 'national_id_card':
      return t('uploadDocuments.slots.nationalIdFront');
    case 'drivers_license':
      return t('uploadDocuments.slots.driversLicenseFront');
    case 'residence_permit':
      return t('uploadDocuments.slots.residencePermitFront');
  }
}

function getIdBackLabel(docType: DocumentType, t: TFunction): string {
  switch (docType) {
    case 'national_id_card':
      return t('uploadDocuments.slots.nationalIdBack');
    case 'drivers_license':
      return t('uploadDocuments.slots.driversLicenseBack');
    case 'residence_permit':
      return t('uploadDocuments.slots.residencePermitBack');
    default:
      return t('uploadDocuments.slots.idBackGeneric');
  }
}

// ---------------------------------------------------------------------------
// Slot state
// ---------------------------------------------------------------------------

type SlotStatus = 'pending' | 'selected' | 'uploading' | 'confirming' | 'uploaded' | 'error';

/** Whether a slot is showing the file-upload UI or the camera-capture UI. */
type SlotInputMode = 'upload' | 'camera';

interface SlotState {
  file: File | null;
  status: SlotStatus;
  error: string | null;
  /** Upload ID returned by the server, included in error messages for support reference. */
  uploadId: string | null;
  /** Current input mode for this slot. */
  inputMode: SlotInputMode;
}

function createInitialSlotStates(slots: UploadSlot[]): Record<string, SlotState> {
  const initial: Record<string, SlotState> = {};
  for (const slot of slots) {
    initial[slot.key] = { file: null, status: 'pending', error: null, uploadId: null, inputMode: 'upload' };
  }
  return initial;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface UploadDocumentsPageProps {
  context: ApplicantContext;
  /** Whether proof of address is required. Defaults to false (optional). */
  requireProofOfAddress?: boolean;
}

export function UploadDocumentsPage({
  context,
  requireProofOfAddress = false,
}: UploadDocumentsPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { caseId } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();
  const submissionSetId = searchParams.get('ss');

  const documentTypes = useMemo(() => getDocumentTypes(t), [t]);

  // Document type selection
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);

  // Proof of address disclosure
  const [poaExpanded, setPoaExpanded] = useState(false);

  // Compute slots from selected document type
  const documentSlots = useMemo(
    () => getDocumentSlots(selectedDocType, requireProofOfAddress, t),
    [selectedDocType, requireProofOfAddress, t],
  );

  // Slot upload states -- reset when document type changes
  const [slots, setSlots] = useState<Record<string, SlotState>>({});

  const handleDocTypeChange = useCallback(
    (docType: DocumentType) => {
      setSelectedDocType(docType);
      const newSlots = getDocumentSlots(docType, requireProofOfAddress, t);
      setSlots(createInitialSlotStates(newSlots));
    },
    [requireProofOfAddress, t],
  );

  const [globalError, setGlobalError] = useState<string | null>(null);

  const updateSlot = useCallback(
    (key: string, patch: Partial<SlotState>) =>
      setSlots((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } })),
    [],
  );

  /** Toggle between file-upload and camera-capture input modes for a slot. */
  const setSlotInputMode = useCallback(
    (key: string, mode: SlotInputMode) =>
      updateSlot(key, { inputMode: mode }),
    [updateSlot],
  );

  /** Handle a successful camera capture: convert the Blob to a File and select it. */
  const handleCameraCapture = useCallback(
    (key: string, blob: Blob) => {
      const file = new File([blob], `${key}-capture.jpg`, { type: 'image/jpeg' });
      updateSlot(key, {
        file,
        status: 'selected',
        error: null,
        uploadId: null,
        inputMode: 'upload', // Switch back to upload view to show the selected file
      });
    },
    [updateSlot],
  );

  /** Handle the fallback file upload from the camera component. */
  const handleCameraFallbackUpload = useCallback(
    (key: string, file: File) => {
      updateSlot(key, {
        file,
        status: 'selected',
        error: null,
        uploadId: null,
        inputMode: 'upload',
      });
    },
    [updateSlot],
  );

  // Upload a single file for a given slot using the two-step flow:
  // 1. requestUploadUrl -> get uploadId
  // 2. confirmUpload -> confirm the upload is complete
  const uploadFile = useCallback(
    async (slot: UploadSlot) => {
      const state = slots[slot.key];
      if (!state?.file || !caseId) return;

      updateSlot(slot.key, { status: 'uploading', error: null, uploadId: null });

      let uploadId: string | null = null;

      try {
        const client = getApiClient();

        let ssId = submissionSetId;
        if (!ssId) {
          const ssResp = await client.applicant.createSubmissionSet(caseId);
          ssId = ssResp.data.id;
        }

        // Step 1: Request upload URL and obtain uploadId.
        const urlResp = await client.applicant.requestUploadUrl(ssId, {
          evidenceType: slot.evidenceType,
          mimeType: state.file.type,
          fileName: state.file.name,
        });

        uploadId = urlResp.data.uploadId;

        // Step 2: PUT the file to the signed URL.
        await uploadFileToSignedUrl(urlResp.data.signedUrl, state.file);

        updateSlot(slot.key, { status: 'confirming', uploadId });

        // Step 3: Confirm the upload is complete.
        await client.applicant.confirmUpload(uploadId, {
          fileSizeBytes: state.file.size,
        });

        updateSlot(slot.key, { status: 'uploaded' });
      } catch (err: unknown) {
        const apiErr = err as ApiClientError;
        const supportRef = uploadId ? ` (ref: ${uploadId})` : '';
        updateSlot(slot.key, {
          status: 'error',
          error: (apiErr.message ?? t('errors.uploadFailed')) + supportRef,
          uploadId,
        });
      }
    },
    [slots, caseId, submissionSetId, updateSlot, t],
  );

  // Upload all files that are selected or have errored and need a retry.
  const handleUploadAll = useCallback(async () => {
    setGlobalError(null);
    const toUpload = documentSlots.filter(
      (s) => slots[s.key]?.file && (slots[s.key].status === 'selected' || slots[s.key].status === 'error'),
    );
    await Promise.all(toUpload.map((s) => uploadFile(s)));
  }, [documentSlots, slots, uploadFile]);

  const requiredSlotsFilled = documentSlots
    .filter((s) => s.required)
    .every((s) => slots[s.key]?.status === 'uploaded');

  const anyUploading = Object.values(slots).some(
    (s) => s.status === 'uploading' || s.status === 'confirming',
  );

  const hasFilesReadyToUpload = documentSlots.some(
    (s) => slots[s.key]?.file && (slots[s.key].status === 'selected' || slots[s.key].status === 'error'),
  );

  const handleContinue = useCallback(() => {
    if (!caseId) return;
    const ssParam = submissionSetId ? `?ss=${submissionSetId}` : '';
    navigate(`/case/${caseId}/selfie${ssParam}`);
  }, [caseId, submissionSetId, navigate]);

  // Missing case ID.
  if (!caseId) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <main className="flex flex-1 items-center justify-center">
          <div className="rounded-lg border border-hairline bg-surface-raised p-8 text-center" role="alert">
            <h2 className="mb-2 text-xl font-bold text-ink">{t('app.missingCase')}</h2>
            <p className="mb-4 text-sm text-ink-muted">
              {t('app.missingCaseDescription')}
            </p>
            <Link
              to="/"
              className="inline-block rounded-lg px-6 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--nv-primary-color)' }}
            >
              {t('app.goToStart')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-2xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('app.identityVerification')}
          </h1>
        </div>
      </header>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-2xl px-4">
        <StepIndicator currentStep="documents" />
      </div>

      <main className="flex-1">
        <div className="mx-auto max-w-2xl px-4 py-8">
          {/* Back button */}
          <button
            type="button"
            onClick={() => {
              const ssParam = submissionSetId ? `?ss=${submissionSetId}` : '';
              navigate(`/case/${caseId}/details${ssParam}`);
            }}
            className="mb-6 inline-flex h-11 items-center gap-1.5 rounded-lg border border-hairline bg-white px-4 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken hover:text-ink focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('app.back')}
          </button>

          {/* Page title */}
          <div className="mb-6 text-center">
            <h2 className="font-display text-3xl font-medium tracking-tight text-ink">
              {t('uploadDocuments.title')}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              {t('uploadDocuments.description')}
            </p>
          </div>

          {/* Info banner */}
          <div
            className="mb-6 flex items-start gap-3 rounded-md border border-info/30 bg-info-tint px-4 py-3"
            role="note"
          >
            <svg
              className="mt-0.5 h-5 w-5 shrink-0 text-info"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
            <p className="text-sm text-info">
              {t('uploadDocuments.infoBanner')}
            </p>
          </div>

          {/* Document type selector -- visual cards */}
          <fieldset className="mb-6">
            <legend className="mb-3 text-sm font-medium text-ink-muted">
              {t('uploadDocuments.documentTypeLegend')}
            </legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {documentTypes.map((dt) => {
                const IconComponent = dt.icon;
                const isSelected = selectedDocType === dt.value;

                return (
                  <label
                    key={dt.value}
                    className={[
                      'group relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-5 text-center transition-all duration-200',
                      isSelected
                        ? 'border-[var(--nv-primary-color)] bg-primary-tint'
                        : 'border-hairline bg-white hover:border-hairline-strong hover:shadow-sm',
                    ].join(' ')}
                  >
                    <input
                      type="radio"
                      name="documentType"
                      value={dt.value}
                      checked={isSelected}
                      onChange={() => handleDocTypeChange(dt.value)}
                      className="sr-only"
                    />
                    {/* Selection indicator */}
                    {isSelected && (
                      <span
                        className="absolute end-3 top-3 flex h-5 w-5 items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: 'var(--nv-primary-color)' }}
                        aria-hidden="true"
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                    <div
                      className={[
                        'flex h-12 w-12 items-center justify-center rounded-lg transition-colors duration-200',
                        isSelected ? 'bg-white' : 'bg-surface-sunken group-hover:bg-surface-sunken',
                      ].join(' ')}
                    >
                      <IconComponent
                        className={[
                          'h-7 w-7 transition-colors duration-200',
                          isSelected ? 'text-[var(--nv-primary-color)]' : 'text-ink-muted group-hover:text-ink-muted',
                        ].join(' ')}
                      />
                    </div>
                    <div>
                      <span
                        className={[
                          'block text-sm font-semibold transition-colors duration-200',
                          isSelected ? 'text-ink' : 'text-ink-muted',
                        ].join(' ')}
                      >
                        {dt.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-ink-muted">
                        {dt.subtitle}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* Upload slots */}
          {selectedDocType && (
            <div className="space-y-6">
              {documentSlots.map((slot) => {
                const state = slots[slot.key];
                if (!state) return null;

                // Check if this is the PoA slot
                const isPoaSlot = slot.key === 'poa';

                // Render PoA slot with collapsible "Why do I need this?"
                if (isPoaSlot) {
                  return (
                    <div
                      key={slot.key}
                      className="rounded-lg border border-hairline bg-white p-6"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-ink">
                          {slot.label}{slot.required ? t('uploadDocuments.slots.requiredSuffix') : t('uploadDocuments.slots.optionalSuffix')}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setPoaExpanded((prev) => !prev)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted transition-colors hover:text-ink-muted"
                          aria-expanded={poaExpanded}
                        >
                          {t('uploadDocuments.whyNeedThis')}
                          <svg
                            className={[
                              'h-3.5 w-3.5 transition-transform duration-200',
                              poaExpanded ? 'rotate-180' : '',
                            ].join(' ')}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                            aria-hidden="true"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      {poaExpanded && (
                        <div className="mb-4 rounded-lg border border-hairline bg-surface-sunken px-4 py-3 text-xs text-ink-muted">
                          <p>
                            {t('uploadDocuments.poaIntro')}
                          </p>
                          <ul className="ms-4 mt-1 list-disc space-y-0.5">
                            <li>{t('uploadDocuments.poaItems.utilityBill')}</li>
                            <li>{t('uploadDocuments.poaItems.bankStatement')}</li>
                            <li>{t('uploadDocuments.poaItems.governmentLetter')}</li>
                            <li>{t('uploadDocuments.poaItems.taxAssessment')}</li>
                          </ul>
                        </div>
                      )}
                      <FileUpload
                        id={`upload-${slot.key}`}
                        label={`${slot.label}${slot.required ? t('uploadDocuments.slots.requiredLabelSuffix') : t('uploadDocuments.slots.optionalLabelSuffix')}`}
                        file={state.file}
                        onFileChange={(f) =>
                          updateSlot(slot.key, {
                            file: f,
                            status: f ? 'selected' : 'pending',
                            error: null,
                            uploadId: null,
                          })
                        }
                        error={state.error}
                        disabled={
                          state.status === 'uploading' ||
                          state.status === 'confirming' ||
                          state.status === 'uploaded'
                        }
                      />
                      {/* Status badges */}
                      {state.status === 'uploading' && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-primary">
                          <span
                            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent"
                            aria-hidden="true"
                          />
                          {t('uploadDocuments.status.uploading')}
                        </p>
                      )}
                      {state.status === 'confirming' && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-primary">
                          <span
                            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent"
                            aria-hidden="true"
                          />
                          {t('uploadDocuments.status.confirming')}
                        </p>
                      )}
                      {state.status === 'uploaded' && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-primary">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {t('uploadDocuments.status.uploaded')}
                        </p>
                      )}
                    </div>
                  );
                }

                // Standard upload slot
                const isSlotBusy =
                  state.status === 'uploading' ||
                  state.status === 'confirming' ||
                  state.status === 'uploaded';
                const isSelfieSlot = slot.key === 'selfie';

                return (
                  <div
                    key={slot.key}
                    className="rounded-lg border border-hairline bg-white p-6"
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-ink">
                        {slot.label}{slot.required ? t('uploadDocuments.slots.requiredSuffix') : t('uploadDocuments.slots.optionalSuffix')}
                      </h3>

                      {/* Input mode toggle -- not shown for selfie (camera is on SelfiePage) or when already uploaded */}
                      {!isSelfieSlot && !isSlotBusy && (
                        <div className="flex rounded-lg border border-hairline p-0.5" role="radiogroup" aria-label={t('uploadDocuments.inputMode.ariaLabel', { label: slot.label })}>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={state.inputMode === 'upload'}
                            onClick={() => setSlotInputMode(slot.key, 'upload')}
                            className={[
                              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200',
                              state.inputMode === 'upload'
                                ? 'bg-[var(--nv-primary-color)] text-white'
                                : 'text-ink-muted hover:text-ink-muted',
                            ].join(' ')}
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-8m0 0l-3 3m3-3l3 3M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5" />
                            </svg>
                            {t('uploadDocuments.inputMode.upload')}
                          </button>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={state.inputMode === 'camera'}
                            onClick={() => setSlotInputMode(slot.key, 'camera')}
                            className={[
                              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200',
                              state.inputMode === 'camera'
                                ? 'bg-[var(--nv-primary-color)] text-white'
                                : 'text-ink-muted hover:text-ink-muted',
                            ].join(' ')}
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                            </svg>
                            {t('uploadDocuments.inputMode.camera')}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* File upload mode */}
                    {(state.inputMode === 'upload' || isSelfieSlot) && (
                      <FileUpload
                        id={`upload-${slot.key}`}
                        label={`${slot.label}${slot.required ? t('uploadDocuments.slots.requiredLabelSuffix') : t('uploadDocuments.slots.optionalLabelSuffix')}`}
                        file={state.file}
                        onFileChange={(f) =>
                          updateSlot(slot.key, {
                            file: f,
                            status: f ? 'selected' : 'pending',
                            error: null,
                            uploadId: null,
                          })
                        }
                        accept={
                          isSelfieSlot
                            ? 'image/jpeg,image/png,image/webp'
                            : undefined
                        }
                        error={state.error}
                        disabled={isSlotBusy}
                      />
                    )}

                    {/* Camera capture mode */}
                    {state.inputMode === 'camera' && !isSelfieSlot && !isSlotBusy && (
                      <DocumentCapture
                        documentType={slot.label}
                        onCapture={(blob) => handleCameraCapture(slot.key, blob)}
                        onFallbackUpload={(file) => handleCameraFallbackUpload(slot.key, file)}
                        onClose={() => setSlotInputMode(slot.key, 'upload')}
                      />
                    )}

                    {/* Status badges */}
                    {state.status === 'uploading' && (
                      <p className="mt-2 flex items-center gap-1 text-xs text-primary">
                        <span
                          className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent"
                          aria-hidden="true"
                        />
                        Uploading...
                      </p>
                    )}
                    {state.status === 'confirming' && (
                      <p className="mt-2 flex items-center gap-1 text-xs text-primary">
                        <span
                          className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent"
                          aria-hidden="true"
                        />
                        Confirming...
                      </p>
                    )}
                    {state.status === 'uploaded' && (
                      <p className="mt-2 flex items-center gap-1 text-xs text-primary">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Uploaded successfully
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Prompt to select a document type */}
          {!selectedDocType && (
            <div className="rounded-lg border-2 border-dashed border-hairline-strong bg-white px-6 py-12 text-center">
              <svg
                className="mx-auto mb-3 h-10 w-10 text-ink-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
              <p className="text-sm text-ink-muted">
                {t('uploadDocuments.selectDocumentTypePrompt')}
              </p>
            </div>
          )}

          {globalError && (
            <div
              role="alert"
              className="mt-6 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
            >
              {globalError}
            </div>
          )}

          {/* Action buttons */}
          {selectedDocType && (
            <div className="mt-8 rounded-lg border border-hairline bg-white p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-ink-muted">
                  {t('uploadDocuments.step')}
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  {/* Upload selected files */}
                  {!requiredSlotsFilled && (
                    <button
                      type="button"
                      onClick={handleUploadAll}
                      disabled={anyUploading || !hasFilesReadyToUpload}
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-hairline-strong px-6 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {anyUploading && (
                        <span
                          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-hairline-dark border-t-transparent"
                          aria-hidden="true"
                        />
                      )}
                      {anyUploading ? t('uploadDocuments.uploading') : t('uploadDocuments.uploadFiles')}
                    </button>
                  )}

                  {/* Continue to selfie */}
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!requiredSlotsFilled}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-md px-6 font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--nv-primary-color)',
                      '--tw-ring-color': 'var(--nv-primary-color)',
                    } as React.CSSProperties}
                  >
                    {t('uploadDocuments.continueToSelfie')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
